import React, { useContext } from "react"
import { observer } from "mobx-react"
import {
  Anchor,
  Box,
  Burger,
  Button,
  Drawer,
  Group,
  Image,
  Menu,
  Stack,
  Text,
  UnstyledButton
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconChevronDown, IconSearch } from "@tabler/icons-react"
import { spotlight } from "@mantine/spotlight"
import { AppWorkspaceContext } from "../stores/AppWorkspace"
import {
  AGENTS_PATH,
  AGENTS_SLUG,
  BTCPAY_FRAMEWORK_ID,
  FRAMEWORKS_HASH,
  GITHUB_URL,
  integrationPath,
  NWC_PATH,
  OWN_PATH_GUIDES,
  platformPath,
  PLATFORMS_PATH,
  processorPath,
  PROCESSORS_PATH,
  SITE_PAGES,
  SWAP_PROVIDER_PATH
} from "../helpers/constants"
import { FRAMEWORKS, LIVE_PLATFORMS } from "../../docs/catalog"
import { LIVE_PROCESSORS } from "../../processors/catalog"
import { captureFrameworkSelect } from "../../analytics"

type NavItem = {
  label: string
  href: string
  onClick?: () => void
  external?: boolean
}

export const SiteHeader: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext)
  const [opened, { toggle, close }] = useDisclosure(false)

  // One entry per framework landing page, in the contract's order. On
  // desktop they hang off a "Frameworks" menu; in the drawer they are listed.
  const frameworkItems: NavItem[] = FRAMEWORKS.map((framework) => ({
    label: framework.label,
    href: integrationPath(framework.id),
    onClick: () => {
      captureFrameworkSelect(framework.id, "site_header")
      workspace.goIntegration(framework.id)
    }
  }))

  // /platforms (contract v8), beside the Frameworks menu: a plain link on
  // desktop, and in the drawer the platform pages listed like the frameworks.
  const platformsItem: NavItem = {
    label: "Platforms",
    href: PLATFORMS_PATH,
    onClick: () => workspace.goPlatforms()
  }
  const platformItems: NavItem[] = [
    ...LIVE_PLATFORMS.map((platform) => ({
      label: platform.label,
      href: platformPath(platform.id),
      onClick: () => workspace.goPlatforms(platform.id)
    })),
    { ...platformsItem, label: "All platforms" }
  ]

  // The payment processor pages, in the drawer only: the desktop row is
  // already as wide as it fits (see .or-nav-desktop), and the home page's
  // PAYMENT PROCESSORS section links them there.
  const processorItems: NavItem[] = [
    ...LIVE_PROCESSORS.map((processor) => ({
      label: processor.label,
      href: processorPath(processor.id),
      onClick: () => workspace.goProcessors(processor.id)
    })),
    { label: "All payment processors", href: PROCESSORS_PATH, onClick: () => workspace.goProcessors() }
  ]

  const items: NavItem[] = [
    {
      label: "API docs",
      href: "/api_docs",
      onClick: () => workspace.goApiDocs()
    },
    {
      label: "Guides",
      href: "/guides",
      onClick: () => workspace.goGuides()
    },
    {
      label: "BTCPay Server",
      href: integrationPath(BTCPAY_FRAMEWORK_ID),
      onClick: () => workspace.goIntegration(BTCPAY_FRAMEWORK_ID)
    },
    {
      label: "Agents",
      href: AGENTS_PATH,
      onClick: () => workspace.goGuides(AGENTS_SLUG)
    },
    { label: "Get NWC code", href: NWC_PATH },
    { label: "Swap provider", href: SWAP_PROVIDER_PATH },
    { label: "GitHub", href: GITHUB_URL, external: true }
  ]

  const isActive = (href: string) => {
    if (href.startsWith("/api_docs")) return workspace.openPage === SITE_PAGES.API_DOCS
    // /agents renders through the guides browser under its own path, so that
    // nav entry splits on the slug rather than the page.
    const ownPath = OWN_PATH_GUIDES[workspace.guides.slug ?? ""]
    if (href === AGENTS_PATH) {
      return workspace.openPage === SITE_PAGES.GUIDES && ownPath === href
    }
    if (href === integrationPath(BTCPAY_FRAMEWORK_ID)) {
      return workspace.openPage === SITE_PAGES.INTEGRATION && workspace.frameworkId === BTCPAY_FRAMEWORK_ID
    }
    if (href === "/guides") {
      return workspace.openPage === SITE_PAGES.GUIDES && !ownPath
    }
    if (href === PLATFORMS_PATH) {
      return workspace.openPage === SITE_PAGES.PLATFORMS && !workspace.platformId
    }
    if (href.startsWith(`${PLATFORMS_PATH}/`)) {
      return workspace.openPage === SITE_PAGES.PLATFORMS && href === platformPath(workspace.platformId ?? "")
    }
    if (href === PROCESSORS_PATH) {
      return workspace.openPage === SITE_PAGES.PROCESSORS && !workspace.processorId
    }
    if (href.startsWith(`${PROCESSORS_PATH}/`)) {
      return workspace.openPage === SITE_PAGES.PROCESSORS && href === processorPath(workspace.processorId ?? "")
    }
    if (href.startsWith("/#") || href.startsWith("/?")) return workspace.openPage === SITE_PAGES.HOME
    if (href.startsWith("/integrations/")) {
      return (
        workspace.openPage === SITE_PAGES.INTEGRATION &&
        href === integrationPath(workspace.frameworkId ?? "")
      )
    }
    return false
  }

  const frameworksActive = workspace.openPage === SITE_PAGES.INTEGRATION

  // Desktop: the framework pages under one menu, so the bar stays one row.
  const frameworksMenu = (
    <Menu shadow="md" width={220} trigger="click-hover" openDelay={80} closeDelay={160}>
      <Menu.Target>
        <UnstyledButton
          className={frameworksActive ? "or-nav-link or-nav-menu is-active" : "or-nav-link or-nav-menu"}
          aria-haspopup="menu"
        >
          Frameworks <IconChevronDown size={14} aria-hidden="true" />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {frameworkItems.map((item) => (
          <Menu.Item
            key={item.label}
            component="a"
            href={item.href}
            onClick={(event: React.MouseEvent) => {
              event.preventDefault()
              item.onClick?.()
            }}
          >
            {item.label}
          </Menu.Item>
        ))}
        <Menu.Divider />
        <Menu.Item
          component="a"
          href={`/#${FRAMEWORKS_HASH}`}
          onClick={(event: React.MouseEvent) => {
            event.preventDefault()
            workspace.goHome(FRAMEWORKS_HASH)
          }}
        >
          All frameworks
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )

  const link = (item: NavItem) => (
    <Anchor
      key={item.label}
      href={item.href}
      className={isActive(item.href) ? "or-nav-link is-active" : "or-nav-link"}
      target={item.external ? "_blank" : undefined}
      rel={item.external ? "noopener noreferrer" : undefined}
      onClick={(event) => {
        if (item.external || !item.onClick) return
        event.preventDefault()
        item.onClick()
        close()
      }}
    >
      {item.label}
    </Anchor>
  )

  return (
    <header className="or-header">
      <Group justify="space-between" wrap="nowrap" h="100%">
        <UnstyledButton
          component="a"
          href="/"
          className="or-brand"
          aria-label="OpenReceive home"
          onClick={(event) => {
            event.preventDefault()
            workspace.goHome()
            close()
          }}
        >
          {/* The wordmark spells the name itself, so no <Text> beside it; the
              link carries the accessible name. Only a bootstrap without one
              falls back to the OR mark plus type. */}
          {workspace.wordmarkUrl ? (
            /* 156x52 is the file's exact 3:1 ratio, so the box is reserved
               correctly and the lettering lands at ~22px. */
            <Image src={workspace.wordmarkUrl} alt="" w={156} h={52} />
          ) : (
            <>
              {workspace.markUrl ? (
                <Image src={workspace.markUrl} alt="" w={40} h={40} radius={4} />
              ) : (
                <Box className="or-brand-fallback">OR</Box>
              )}
              <Text fw={800}>OpenReceive</Text>
            </>
          )}
        </UnstyledButton>

        {/* Between the md breakpoint and 1100px the full row does not fit
            beside the wordmark, so spa.css tightens the gaps and swaps the
            labelled Search button for the icon-only one (.or-nav-desktop). */}
        <Group gap="md" visibleFrom="md" className="or-nav-desktop" wrap="nowrap">
          {frameworksMenu}
          {link(platformsItem)}
          {items.map(link)}
          <Button
            className="or-nav-search-full"
            variant="light"
            leftSection={<IconSearch size={16} />}
            onClick={() => spotlight.open()}
          >
            Search
          </Button>
          <Button
            className="or-nav-search-icon"
            variant="light"
            onClick={() => spotlight.open()}
            aria-label="Search docs"
          >
            <IconSearch size={16} />
          </Button>
        </Group>

        <Group gap="sm" hiddenFrom="md">
          <Button
            variant="light"
            size="sm"
            onClick={() => spotlight.open()}
            aria-label="Search docs"
          >
            <IconSearch size={16} />
          </Button>
          <Burger opened={opened} onClick={toggle} aria-label="Open navigation" />
        </Group>
      </Group>

      <Drawer opened={opened} onClose={close} title="OpenReceive" position="right">
        <Stack gap="md">
          <Text className="or-eyebrow" mb={-8}>
            Frameworks
          </Text>
          {frameworkItems.map(link)}
          <Text className="or-eyebrow" mb={-8}>
            Platforms
          </Text>
          {platformItems.map(link)}
          <Text className="or-eyebrow" mb={-8}>
            Payment processors
          </Text>
          {processorItems.map(link)}
          <Text className="or-eyebrow" mb={-8}>
            Docs
          </Text>
          {items.map(link)}
        </Stack>
      </Drawer>
    </header>
  )
})
